import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import api from '../services/api'
import { avisar } from '../components/Avisos'

const PRIMARY = 'var(--primary)'
const SECONDARY = '#745700'

const mesesAbrev = ['JAN','FEV','MAR','ABR','MAI','JUN','JUL','AGO','SET','OUT','NOV','DEZ']
const mesesCompletos = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro']
const PALETA_RANKING = [PRIMARY, SECONDARY, '#AFADAC', '#4C7EF3', '#2FA88A', '#B84DCB']

function saudacao() {
  const h = new Date().getHours()
  if (h < 12) return 'Bom dia'
  if (h < 18) return 'Boa tarde'
  return 'Boa noite'
}

const fmtInteiro = (v) => (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
const ROTULO_MES_H = 30 // altura do rotulo do mes embaixo das barras do grafico Producao Mensal
const fmtMoeda = (v) => `R$ ${(Number(v) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

export default function Dashboard() {
  const navigate = useNavigate()
  const { usuario, ehGestor } = useAuth()
  const [dados, setDados] = useState(null)
  const areaProducaoRef = useRef(null)
  const [alturaAreaProducao, setAlturaAreaProducao] = useState(260)
  useEffect(() => {
    const el = areaProducaoRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => setAlturaAreaProducao(el.clientHeight || 260))
    ro.observe(el)
    return () => ro.disconnect()
  })
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState(null)

  const hoje = new Date()

  // Dashboard e restrito ao Administrador — quem nao for admin (link direto, favorito antigo etc.)
  // e mandado pra Clientes assim que o usuario logado carregar.
  useEffect(() => {
    if (usuario && !ehGestor) navigate('/home', { replace: true })
  }, [usuario])

  // Seletor UNICO da tela: periodo (Mensal, alternando mes a mes | Anual, alternando ano a ano) +
  // metrica (Produção/Comissão/Qtde. Documentos). Controla tanto os cards de Metas (/dashboard/painel)
  // quanto Produção Mensal/Ranking das Seguradoras (/dashboard) — antes eram 2 seletores separados.
  const [painel, setPainel] = useState(null)
  const [painelPeriodo, setPainelPeriodo] = useState('mensal') // 'mensal' | 'anual'
  const [painelMetrica, setPainelMetrica] = useState('producao') // 'producao' | 'comissao' | 'documentos'
  const [painelAno, setPainelAno] = useState(hoje.getFullYear())
  const [painelMes, setPainelMes] = useState(hoje.getMonth() + 1)

  useEffect(() => {
    setLoading(true)
    setErro(null)
    const params = new URLSearchParams({ periodo: painelPeriodo, metrica: painelMetrica, ano: painelAno })
    if (painelPeriodo === 'mensal') params.set('mes', painelMes)
    api.get(`/dashboard?${params}`)
      .then(r => setDados(r.data))
      .catch(err => setErro(err.response?.data?.erro || err.message))
      .finally(() => setLoading(false))
  }, [painelPeriodo, painelMetrica, painelAno, painelMes])

  const buscarPainel = () => {
    const params = new URLSearchParams({ periodo: painelPeriodo, metrica: painelMetrica, ano: painelAno })
    if (painelPeriodo === 'mensal') params.set('mes', painelMes)
    return api.get(`/dashboard/painel?${params}`).then(r => setPainel(r.data))
  }
  useEffect(() => { buscarPainel() }, [painelPeriodo, painelMetrica, painelAno, painelMes])

  const painelAnterior = () => {
    if (painelPeriodo === 'anual') { setPainelAno(a => a - 1); return }
    if (painelMes === 1) { setPainelAno(a => a - 1); setPainelMes(12) } else { setPainelMes(m => m - 1) }
  }
  const painelProximo = () => {
    if (painelPeriodo === 'anual') { setPainelAno(a => a + 1); return }
    if (painelMes === 12) { setPainelAno(a => a + 1); setPainelMes(1) } else { setPainelMes(m => m + 1) }
  }

  // Modal "Gerar Metas"
  const [modalMetas, setModalMetas] = useState(false)
  const [metasInfo, setMetasInfo] = useState(null) // { ativa, itens }
  const [crescimentoInput, setCrescimentoInput] = useState('0')
  const [gerandoMetas, setGerandoMetas] = useState(false)

  useEffect(() => {
    if (!modalMetas) return
    api.get('/metas').then(r => {
      setMetasInfo(r.data)
      setCrescimentoInput(String(r.data.itens?.[0]?.crescimento_pct ?? '0').replace('.', ','))
    })
  }, [modalMetas])

  const alternarAtivarMetas = async () => {
    const nova = !metasInfo.ativa
    await api.patch('/metas/ativar', { ativa: nova })
    setMetasInfo(m => ({ ...m, ativa: nova }))
    buscarPainel()
  }

  const gerarMetas = async () => {
    setGerandoMetas(true)
    try {
      const { data } = await api.post('/metas/gerar', {
        crescimento_pct: crescimentoInput.replace(',', '.'), ano: painelAno, mes: painelMes,
      })
      setMetasInfo(m => ({ ...m, itens: data.itens }))
      buscarPainel()
    } catch (err) {
      avisar('Erro ao gerar metas: ' + (err.response?.data?.erro || err.message))
    } finally {
      setGerandoMetas(false)
    }
  }

  if (usuario && !ehGestor) return null
  if (loading) return <div className="page-content" style={{ fontFamily: 'Manrope, sans-serif' }}>Carregando...</div>
  if (erro) return (
    <div className="page-content" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <div className="alert-error" style={{ padding: '12px 16px', borderRadius: 8 }}>Erro ao carregar o Dashboard: {erro}</div>
    </div>
  )
  if (!dados) return null

  const dataFormatada = hoje.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })

  const producaoMensal = dados.producao_mensal || []
  // Produção Mensal segue a métrica do seletor (Produção / Comissão / Qtde. Documentos).
  const fmtValorProducao = (v) => dados.metrica === 'documentos' ? fmtInteiro(v) : fmtMoeda(v)
  const descricaoProducao = dados.metrica === 'documentos' ? 'Quantidade de documentos emitidos'
    : dados.metrica === 'comissao' ? 'Comissão dos documentos emitidos' : 'Prêmio líquido emitido'
  const maxProducao = arredondarTeto(Math.max(1, ...producaoMensal.flatMap(p => [p.valor_atual, p.valor_ano_anterior])))
  const ticksProducao = [1, 0.75, 0.5, 0.25, 0].map(f => Math.round(maxProducao * f))
  // Altura das barras = altura da area do grafico - rotulo do mes embaixo - espaco pros valores em cima
  const altBarras = Math.max(120, alturaAreaProducao - ROTULO_MES_H - 22)
  const fmtTickProducao = (v) => v >= 1000 ? `${Math.round(v / 1000)}k` : `${v}`
  // Valor em cima de cada barra: curto pra caber nas 12 colunas ("36,2k"); o valor cheio fica no title.
  const fmtValorBarra = (v) => {
    const n = Number(v) || 0
    if (!n) return ''
    return Math.abs(n) >= 1000
      ? `${(n / 1000).toLocaleString('pt-BR', { maximumFractionDigits: n >= 100000 ? 0 : 1 })}k`
      : Math.round(n).toLocaleString('pt-BR')
  }

  // Ranking: top 5 seguradoras individualmente + o resto agrupado em "Outras", pra nao lotar o
  // donut de fatia minuscula quando ha muitas seguradoras com pouca producao no mes.
  const rankingBruto = dados.ranking_seguradoras || []
  const rankingTop = rankingBruto.slice(0, 5)
  const rankingResto = rankingBruto.slice(5)
  const rankingExibido = rankingResto.length > 0
    ? [...rankingTop, {
        seguradora: 'Outras',
        total: rankingResto.reduce((s, r) => s + r.total, 0),
        pct: rankingResto.reduce((s, r) => s + r.pct, 0),
      }]
    : rankingTop
  const rankingComCor = rankingExibido.map((r, i) => ({ ...r, color: PALETA_RANKING[i % PALETA_RANKING.length] }))
  const liderRanking = rankingComCor[0]

  const cat = painel?.categorias || {}
  const trans = painel?.transmitidos || {}
  const temMeta = !!(painel?.metas_ativas && painel?.meta_valor != null)
  const rotuloComparacao = painelPeriodo === 'anual' ? 'vs. ano anterior' : 'vs. mesmo mês ano anterior'
  const labelTotal = { producao: 'Prêmio Líquido Total', comissao: 'Comissão Total', documentos: 'Total de Documentos' }[painelMetrica]
  const rotuloMetrica = { producao: 'Produção', comissao: 'Comissão', documentos: 'Quantidade de documentos' }[painelMetrica]
  const legendaAtual = painelPeriodo === 'anual' ? `${painelAno}` : `${mesesAbrev[painelMes - 1].toLowerCase()}/${String(painelAno).slice(2)}`
  const legendaAnterior = painelPeriodo === 'anual' ? `${painelAno - 1}` : `${mesesAbrev[painelMes - 1].toLowerCase()}/${String(painelAno - 1).slice(2)}`

  return (
    <div style={{ fontFamily: 'Manrope, sans-serif', minHeight: '100vh' }}>
      <div style={{ padding: '32px 36px 40px' }}>

        {/* Header */}
        <header style={{ marginBottom: 28 }}>
          <p style={{ fontSize: 10, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4 }}>
            {dataFormatada}
          </p>
          <h1 style={{ fontSize: 32, fontWeight: 800, color: '#2F2F2E', margin: 0, letterSpacing: '-0.02em' }}>
            {saudacao()}, {usuario?.nome?.split(' ')[0] || 'Usuário'}
          </h1>
          <p style={{ fontSize: 13, color: SECONDARY, fontWeight: 600, margin: '4px 0 0', letterSpacing: '0.02em' }}>
            Bem-vindo ao Dashboard do Administrador.
          </p>
        </header>

        {/* Metas: producao por categoria + % da meta */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 12 }}>
          <h2 style={{ fontSize: 16, fontWeight: 800, color: '#2F2F2E', margin: 0 }}>Metas do {painelPeriodo === 'anual' ? 'Ano' : 'Mês'}</h2>
          <button onClick={() => setModalMetas(true)} style={{
            padding: '8px 16px', background: '#fff', color: PRIMARY,
            border: `2px solid rgba(var(--primary-rgb),0.15)`, borderRadius: 10,
            fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'Manrope, sans-serif',
          }}>🎯 Gerar Metas</button>
        </div>

        {/* Linha de seletores: Mensal/Anual + mês/ano à esquerda, Métrica à direita da tela */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <SegmentedControl
              valor={painelPeriodo} onChange={setPainelPeriodo}
              opcoes={[{ valor: 'mensal', rotulo: 'Mensal' }, { valor: 'anual', rotulo: 'Anual' }]}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#fff', borderRadius: 12, padding: 6, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <button onClick={painelAnterior} title="Anterior" style={{
                width: 30, height: 30, borderRadius: 8, border: 'none', background: 'none',
                cursor: 'pointer', fontSize: 15, color: PRIMARY, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(var(--primary-rgb),0.08)'}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}
              >‹</button>
              <span style={{ fontSize: 13, fontWeight: 800, color: '#2F2F2E', minWidth: 110, textAlign: 'center' }}>
                {painelPeriodo === 'anual' ? painelAno : `${mesesCompletos[painelMes - 1]} de ${painelAno}`}
              </span>
              <button onClick={painelProximo} title="Próximo" style={{
                width: 30, height: 30, borderRadius: 8, border: 'none', background: 'none',
                cursor: 'pointer', fontSize: 15, color: PRIMARY, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(var(--primary-rgb),0.08)'}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}
              >›</button>
            </div>
          </div>
          <SegmentedControl
            valor={painelMetrica} onChange={setPainelMetrica}
            opcoes={[{ valor: 'producao', rotulo: 'Produção' }, { valor: 'comissao', rotulo: 'Comissão' }, { valor: 'documentos', rotulo: 'Qtde. Documentos' }]}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 14, marginBottom: 32 }}>
          <CategoriaCard label={labelTotal} valor={cat.total?.atual} transmitido={trans.total} variacaoPct={cat.total?.variacao_pct} corBg={PRIMARY} metrica={painelMetrica} rotuloComparacao={rotuloComparacao} />
          <CategoriaCard label="Novos" valor={cat.novos?.atual} transmitido={trans.novos} variacaoPct={cat.novos?.variacao_pct} corBg="#2F6B3A" metrica={painelMetrica} rotuloComparacao={rotuloComparacao} />
          <CategoriaCard label="Renovações" valor={cat.renovacoes?.atual} transmitido={trans.renovacoes} variacaoPct={cat.renovacoes?.variacao_pct} corBg="#1F6F78" metrica={painelMetrica} rotuloComparacao={rotuloComparacao} />
          <CategoriaCard label="Refeitos" valor={cat.refeitos?.atual} transmitido={trans.refeitos} variacaoPct={cat.refeitos?.variacao_pct} corBg="#7B4FA0" metrica={painelMetrica} rotuloComparacao={rotuloComparacao} />
          <CategoriaCard label="Endossos" valor={cat.endossos?.atual} transmitido={trans.endossos} variacaoPct={cat.endossos?.variacao_pct} corBg="#5B6470" metrica={painelMetrica} rotuloComparacao={rotuloComparacao} />
          <MetaCard pct={painel?.pct_meta} temMeta={temMeta} onClick={() => setModalMetas(true)} />
        </div>

        {/* Charts Row */}
        <div style={{ display: 'flex', gap: 20, marginBottom: 24, alignItems: 'stretch' }}>

          {/* Produção Mensal */}
          <div style={{
            flex: 1, background: '#fff', borderRadius: 16, padding: 28,
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', minHeight: 320,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
              <div>
                <h2 style={{ fontSize: 16, fontWeight: 800, color: '#2F2F2E', margin: 0 }}>Produção Mensal</h2>
                <p style={{ fontSize: 12, color: SECONDARY, fontWeight: 600, margin: '3px 0 0' }}>{descricaoProducao} pelo início de vigência, últimos 12 meses x mesmo mês do ano anterior</p>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <LegendaDot color={PRIMARY} label="Este mês" />
                <LegendaDot color="#DFDCDC" label="Ano anterior" />
              </div>
            </div>
            {/* Area do grafico: mede a altura disponivel no card (o conteudo e' absoluto, entao nao
                influencia o tamanho medido) e as barras usam toda ela. */}
            <div ref={areaProducaoRef} style={{ flex: 1, position: 'relative', minHeight: 240 }}>
              <div style={{ position: 'absolute', inset: 0 }}>
                {/* Eixo vertical + linhas de grade, alinhados a base das barras */}
                {ticksProducao.map(v => (
                  <div key={v} style={{ position: 'absolute', left: 0, right: 0, bottom: ROTULO_MES_H + (v / maxProducao) * altBarras, height: 0 }}>
                    <span style={{ position: 'absolute', left: 0, width: 34, top: -6, fontSize: 9, fontWeight: 700, color: '#bbb', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtTickProducao(v)}</span>
                    <div style={{ position: 'absolute', left: 40, right: 0, top: 0, borderTop: v === 0 ? '1px solid #eee' : '1px dashed #f3f3f3' }} />
                  </div>
                ))}
                <div style={{ position: 'absolute', left: 40, right: 0, bottom: 0, top: 0, display: 'flex', gap: 10 }}>
                  {producaoMensal.map((p, i) => {
                    const ativo = i === producaoMensal.length - 1
                    const hAtual = Math.round((Math.max(0, p.valor_atual) / maxProducao) * altBarras)
                    const hAnterior = Math.round((Math.max(0, p.valor_ano_anterior) / maxProducao) * altBarras)
                    return (
                      <div key={`${p.ano}-${p.mes}`} style={{ flex: 1, position: 'relative', minWidth: 0 }}
                        title={`${mesesCompletos[p.mes - 1]}/${p.ano}: ${fmtValorProducao(p.valor_atual)}  ·  ${mesesCompletos[p.mes - 1]}/${p.ano - 1}: ${fmtValorProducao(p.valor_ano_anterior)}`}
                      >
                        {/* Ano anterior: cinza, atras, encostado a esquerda */}
                        {hAnterior > 0 && (<>
                          <div style={{ position: 'absolute', bottom: ROTULO_MES_H, left: 0, width: '64%', height: hAnterior, background: '#DFDCDC', borderRadius: '6px 6px 0 0' }} />
                          <span style={{ position: 'absolute', bottom: ROTULO_MES_H + hAnterior + 2, left: 0, fontSize: 9, fontWeight: 800, color: '#999', whiteSpace: 'nowrap' }}>{fmtValorBarra(p.valor_ano_anterior)}</span>
                        </>)}
                        {/* Este ano: cor primaria, na frente, encostado a direita (sobrepoe parte da cinza) */}
                        {hAtual > 0 && (<>
                          <div style={{ position: 'absolute', bottom: ROTULO_MES_H, right: 0, width: '64%', height: hAtual, background: PRIMARY, borderRadius: '6px 6px 0 0', boxShadow: ativo ? '0 0 0 2px rgba(var(--primary-rgb),0.25)' : 'none' }} />
                          <span style={{ position: 'absolute', bottom: ROTULO_MES_H + hAtual + 2, right: 0, fontSize: 10, fontWeight: 800, color: PRIMARY, whiteSpace: 'nowrap' }}>{fmtValorBarra(p.valor_atual)}</span>
                        </>)}
                        <span style={{
                          position: 'absolute', bottom: 0, left: 0, right: 0, height: ROTULO_MES_H - 6,
                          fontSize: 9, fontWeight: 700, color: ativo ? PRIMARY : '#aaa',
                          letterSpacing: '0.05em', textAlign: 'center', lineHeight: 1.3,
                        }}>{mesesAbrev[p.mes - 1]}<br />{String(p.ano).slice(2)}</span>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Ranking Seguradoras */}
          <div style={{
            width: 340, background: '#EAE7E7', borderRadius: 16, padding: 28,
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column',
          }}>
            <h2 style={{ fontSize: 16, fontWeight: 800, color: '#2F2F2E', margin: '0 0 20px' }}>Ranking das Seguradoras</h2>

            {/* Donut */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
              <div style={{ position: 'relative', width: 160, height: 160 }}>
                <svg width="160" height="160" viewBox="0 0 160 160" style={{ transform: 'rotate(-90deg)' }}>
                  <circle cx="80" cy="80" r="60" fill="transparent" stroke="#DFDCDC" strokeWidth="18" />
                  {(() => {
                    const CIRC = 2 * Math.PI * 60
                    let acumulado = 0
                    return rankingComCor.map(r => {
                      const arco = (r.pct / 100) * CIRC
                      const el = (
                        <circle key={r.seguradora} cx="80" cy="80" r="60" fill="transparent" stroke={r.color}
                          strokeWidth="18" strokeDasharray={`${arco} ${CIRC - arco}`}
                          strokeDashoffset={-((acumulado / 100) * CIRC)} />
                      )
                      acumulado += r.pct
                      return el
                    })
                  })()}
                </svg>
                <div style={{
                  position: 'absolute', inset: 0, display: 'flex',
                  flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '0 20px', textAlign: 'center',
                }}>
                  {liderRanking ? (
                    <>
                      <span style={{ fontSize: 9, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{liderRanking.seguradora}</span>
                      <span style={{ fontSize: 32, fontWeight: 900, color: '#2F2F2E', lineHeight: 1 }}>{liderRanking.pct.toFixed(0)}%</span>
                    </>
                  ) : (
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#aaa' }}>Sem dados no mês</span>
                  )}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {rankingComCor.map(seg => (
                <div key={seg.seguradora} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: seg.color, flexShrink: 0 }} />
                    <span style={{ fontWeight: 600, color: '#2F2F2E' }}>{seg.seguradora}</span>
                  </div>
                  <span style={{ fontWeight: 800, color: seg.color === PRIMARY ? '#2F2F2E' : '#888' }}>{seg.pct.toFixed(0)}%</span>
                </div>
              ))}
              {rankingComCor.length === 0 && (
                <span style={{ fontSize: 12, color: '#888', textAlign: 'center' }}>Nenhuma apólice emitida com início de vigência neste mês.</span>
              )}
            </div>
          </div>
        </div>

        {/* Comparativo: acumulado dia a dia x mesmo mes ano anterior x Meta */}
        <div style={{
          background: '#fff', borderRadius: 16, padding: 28,
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 24,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 800, color: '#2F2F2E', margin: 0 }}>Comparativo</h2>
              <p style={{ fontSize: 12, color: SECONDARY, fontWeight: 600, margin: '3px 0 0' }}>
                {rotuloMetrica} acumulada {painelPeriodo === 'anual' ? 'mês a mês' : 'dia a dia'}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 14 }}>
              <LegendaDot color={PRIMARY} label={legendaAtual} />
              <LegendaDot color={SECONDARY} label={legendaAnterior} />
              {temMeta && <LegendaDot color="#C9A227" label="Meta" />}
            </div>
          </div>
          <GraficoComparativo
            atual={painel?.comparativo?.atual || []}
            anterior={painel?.comparativo?.anterior || []}
            eixoMax={painel?.comparativo?.eixo_max || (painelPeriodo === 'anual' ? 12 : 30)}
            metaValor={temMeta ? painel?.meta_valor : null}
            periodo={painelPeriodo}
          />
        </div>

      </div>

      {modalMetas && (
        <div className="modal-overlay" onClick={() => setModalMetas(false)}>
          <div className="modal" style={{ maxWidth: 620 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Geração de Metas — Próximos 12 meses</span>
              <button className="modal-close" onClick={() => setModalMetas(false)}>×</button>
            </div>
            <p style={{ fontSize: 13, color: '#666', margin: '0 0 18px' }}>
              Com base na produção real do mesmo mês um ano antes, projeta uma meta para os próximos 12 meses a partir de {mesesCompletos[painelMes - 1]}/{painelAno}, aplicando o % de crescimento informado.
            </p>

            {metasInfo && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#2F2F2E' }}>
                <input type="checkbox" checked={metasInfo.ativa} onChange={alternarAtivarMetas} style={{ width: 16, height: 16, accentColor: PRIMARY, cursor: 'pointer' }} />
                Ativar Metas (mostrar % da meta e a linha Meta nos gráficos)
              </label>
            )}

            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 20 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">% de crescimento (vs. mesmo mês do ano anterior)</label>
                <input className="form-input" value={crescimentoInput} onChange={e => setCrescimentoInput(e.target.value)} style={{ maxWidth: 160 }} placeholder="0,00" />
              </div>
              <button className="btn btn-primary" onClick={gerarMetas} disabled={gerandoMetas}>
                {gerandoMetas ? 'Gerando...' : 'Gerar Metas'}
              </button>
            </div>

            {metasInfo?.itens?.length > 0 && (
              <>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#2F2F2E', margin: '0 0 10px' }}>Metas para a Produção Total</h3>
                <div className="table-wrap" style={{ maxHeight: 340, overflowY: 'auto' }}>
                  <table className="table-borda-destaque">
                    <thead>
                      <tr><th>Mês</th><th>Prêmio Base</th><th>Comissão</th><th>Qtde. Docs</th></tr>
                    </thead>
                    <tbody>
                      {metasInfo.itens.map(item => (
                        <tr key={`${item.ano}-${item.mes}`} style={(item.ano === painelAno && item.mes === painelMes) ? { background: 'rgba(var(--primary-rgb),0.06)' } : undefined}>
                          <td style={{ fontWeight: 700 }}>{mesesAbrev[item.mes - 1]}/{String(item.ano).slice(2)}</td>
                          <td style={{ textAlign: 'center' }}>
                            {fmtMoeda(item.premio_base)}
                            <div style={{ fontSize: 10, color: '#3B6D11', fontWeight: 700 }}>+{parseFloat(item.crescimento_pct).toFixed(2)}%</div>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {fmtMoeda(item.comissao)}
                            <div style={{ fontSize: 10, color: '#3B6D11', fontWeight: 700 }}>+{parseFloat(item.crescimento_pct).toFixed(2)}%</div>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {item.qtde_docs}
                            <div style={{ fontSize: 10, color: '#3B6D11', fontWeight: 700 }}>+{parseFloat(item.crescimento_pct).toFixed(2)}%</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {metasInfo && metasInfo.itens?.length === 0 && (
              <p style={{ fontSize: 13, color: '#888', textAlign: 'center', padding: '16px 0' }}>Nenhuma meta gerada ainda. Informe o % de crescimento e clique em "Gerar Metas".</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function LegendaDot({ color, label }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, color: '#888' }}>
      <div style={{ width: 10, height: 10, borderRadius: '50%', background: color }} />
      {label}
    </span>
  )
}

// Alternador tipo "pill" (Mensal/Anual, Produção/Comissão/Qtde. Documentos).
function SegmentedControl({ opcoes, valor, onChange }) {
  return (
    <div style={{ display: 'inline-flex', background: '#fff', borderRadius: 10, padding: 4, gap: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      {opcoes.map(op => (
        <button key={op.valor} type="button" onClick={() => onChange(op.valor)} style={{
          padding: '6px 14px', borderRadius: 7, border: 'none', cursor: 'pointer',
          fontSize: 12, fontWeight: 700, fontFamily: 'Manrope, sans-serif', whiteSpace: 'nowrap',
          background: valor === op.valor ? PRIMARY : 'transparent',
          color: valor === op.valor ? '#fff' : '#888',
          transition: 'background 0.15s, color 0.15s',
        }}>{op.rotulo}</button>
      ))}
    </div>
  )
}

// Card de categoria da producao (Produção Total/Novos/Renovações/Refeitos/Endossos) — valor do
// periodo selecionado + variação % vs periodo anterior, com seta pra cima/baixo. `metrica` decide
// se o valor e formatado como dinheiro (Produção/Comissão) ou como contagem (Qtde. Documentos).
// `transmitido` = propostas cadastradas ainda nao emitidas, mostrado a parte (nao entra no valor).
function CategoriaCard({ label, subtitulo, valor, transmitido, variacaoPct, corBg, metrica, rotuloComparacao }) {
  const v = valor || 0
  const fmt = (n) => metrica === 'documentos' ? fmtInteiro(n) : fmtMoeda(n)
  const valorFormatado = fmt(v)
  const temVariacao = variacaoPct !== undefined && variacaoPct !== null
  const subiu = temVariacao && variacaoPct >= 0
  return (
    <div style={{
      background: corBg, borderRadius: 12, padding: '14px 16px', color: '#fff',
      display: 'flex', flexDirection: 'column', gap: 8, minHeight: 96, justifyContent: 'space-between',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6 }}>
        <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', opacity: 0.9 }}>{label}</span>
        {subtitulo && <span style={{ fontSize: 9.5, fontWeight: 700, opacity: 0.75, whiteSpace: 'nowrap' }}>{subtitulo}</span>}
      </div>
      <div>
        <span style={{ fontSize: 19, fontWeight: 800, lineHeight: 1.1, display: 'block' }}>{valorFormatado}</span>
        <span title="Propostas cadastradas no sistema que ainda não foram emitidas" style={{ fontSize: 11, fontWeight: 600, opacity: 0.85 }}>
          + {fmt(transmitido || 0)} <span style={{ opacity: 0.8, fontWeight: 500 }}>transmitido</span>
        </span>
      </div>
      {temVariacao && (
        <span style={{ fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4, opacity: 0.9 }}>
          {subiu ? '▲' : '▼'} {Math.abs(variacaoPct).toFixed(1)}% <span style={{ opacity: 0.7, fontWeight: 500 }}>{rotuloComparacao}</span>
        </span>
      )}
    </div>
  )
}

// Card "% da meta" — fica ao lado dos cards de categoria, mesma altura/formato.
function MetaCard({ pct, temMeta, onClick }) {
  if (!temMeta || pct === null) {
    return (
      <div onClick={onClick} style={{
        background: '#fff', border: '2px dashed #ddd', borderRadius: 12, padding: '14px 16px',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 4, minHeight: 96, cursor: 'pointer', textAlign: 'center',
      }}>
        <span style={{ fontSize: 20 }}>🎯</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#999' }}>Sem meta definida</span>
      </div>
    )
  }
  let cor = '#A32D2D', texto = 'Muito abaixo da meta'
  if (pct >= 100) { cor = '#2F6B3A'; texto = 'Meta atingida' }
  else if (pct >= 90) { cor = '#1F6F78'; texto = 'Quase lá' }
  else if (pct >= 50) { cor = '#8A5A00'; texto = 'Abaixo da meta' }
  return (
    <div onClick={onClick} style={{
      background: '#fff', border: `2px solid ${cor}33`, borderRadius: 12, padding: '14px 16px',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 2, minHeight: 96, cursor: 'pointer', textAlign: 'center',
    }}>
      <span style={{ fontSize: 22 }}>🏆</span>
      <span style={{ fontSize: 20, fontWeight: 900, color: cor, lineHeight: 1.1 }}>{pct.toFixed(0)}% atingida</span>
      <span style={{ fontSize: 10.5, fontWeight: 700, color: cor, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{texto}</span>
    </div>
  )
}

const arredondarTeto = (v) => {
  if (v <= 0) return 100
  const mag = Math.pow(10, Math.floor(Math.log10(v)))
  return Math.ceil(v / (mag / 2)) * (mag / 2)
}

// Grafico de linha (SVG simples, sem biblioteca): acumulado eixo a eixo (dia do mes, no periodo
// Mensal, ou mes do ano, no periodo Anual) — este periodo x mesmo periodo do ano anterior x reta
// da Meta (0 no primeiro eixo ate a meta no ultimo). `atual`/`anterior` sao arrays ja acumulados,
// posicao i = eixo i+1; `atual` pode ter `null` nos eixos que ainda nao aconteceram.
function GraficoComparativo({ atual, anterior, eixoMax, metaValor, periodo }) {
  const W = 760, H = 260, padL = 46, padR = 12, padT = 10, padB = 22
  const plotW = W - padL - padR, plotH = H - padT - padB

  const maxValor = arredondarTeto(Math.max(
    1,
    metaValor || 0,
    ...anterior,
    ...atual.filter(v => v !== null && v !== undefined),
  ))

  const xFor = (i) => padL + (i / Math.max(1, eixoMax - 1)) * plotW
  const yFor = (valor) => padT + plotH - (Math.max(0, valor) / maxValor) * plotH

  const pontosAtual = atual.map((v, i) => (v === null || v === undefined) ? null : `${xFor(i)},${yFor(v)}`).filter(Boolean).join(' ')
  const pontosAnterior = anterior.map((v, i) => `${xFor(i)},${yFor(v)}`).join(' ')
  const pontosMeta = metaValor ? `${xFor(0)},${yFor(0)} ${xFor(eixoMax - 1)},${yFor(metaValor)}` : ''

  const ticksY = [0, 0.25, 0.5, 0.75, 1].map(f => Math.round(maxValor * f))
  const fmtTick = (v) => v >= 1000 ? `${Math.round(v / 1000)}k` : `${v}`
  const rotulosEixo = periodo === 'anual' ? mesesAbrev : Array.from({ length: eixoMax }, (_, i) => i + 1)

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      {ticksY.map(v => (
        <g key={v}>
          <line x1={padL} x2={W - padR} y1={yFor(v)} y2={yFor(v)} stroke="#F0F0F0" strokeWidth="1" />
          <text x={padL - 8} y={yFor(v) + 3} fontSize="9" fill="#aaa" textAnchor="end">{fmtTick(v)}</text>
        </g>
      ))}
      {rotulosEixo.slice(0, eixoMax).map((r, i) => (
        <text key={i} x={xFor(i)} y={H - 6} fontSize="8" fill="#bbb" textAnchor="middle">{r}</text>
      ))}
      {metaValor > 0 && <polyline points={pontosMeta} fill="none" stroke="#C9A227" strokeWidth="2" />}
      <polyline points={pontosAnterior} fill="none" stroke={SECONDARY} strokeWidth="2" />
      <polyline points={pontosAtual} fill="none" stroke={PRIMARY} strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  )
}
